import type { LiveComponent } from '@use-gpu/live';
import type { VectorLike, Lazy, UniformAttribute, DataBounds } from '@use-gpu/core';
import type { ShaderSource } from '@use-gpu/shader';

import { useDraw } from '../hooks/useDraw';

import { memo, useCallback, useMemo, useNoCallback } from '@use-gpu/live';
import { chainTo } from '@use-gpu/shader/wgsl';

import { FacetSource, useFacetShader } from './hooks/facets';
import { PickingSource, usePickingShader } from './hooks/picking';
import { useScissorShader } from './hooks/scissor';

import { useMaterialContext } from '../providers/material-provider';
import { TransformContextProps } from '../providers/transform-provider';

import { useApplyTransform } from '../hooks/useApplyTransform';
import { useShaderRef } from '../hooks/useShaderRef';
import { useShader, useNoShader } from '../hooks/useShader';
import { useSource } from '../hooks/useSource';
import { useDataLength } from '../hooks/useDataBinding';
import { useInstancedVertex, useNoInstancedVertex } from '../hooks/useInstancedVertex';
import { usePipelineOptions, PipelineOptions } from '../hooks/usePipelineOptions';

import { getQuadVertex, getQuadVertexShaded } from '@use-gpu/wgsl/instance/vertex/quad.wgsl';
import { getMaskedColor } from '@use-gpu/wgsl/mask/masked.wgsl';
import { solidToShaded } from '@use-gpu/wgsl/instance/surface/solid-to-shaded.wgsl';
import { getMaskedSurface } from '@use-gpu/wgsl/instance/surface/masked-surface.wgsl';
import { getRaytraceSurface } from '@use-gpu/wgsl/instance/surface/raytrace-surface.wgsl';

const POSITIONS: UniformAttribute = { format: 'vec4<f32>', name: 'getPosition' };

export type RawQuadsFlags = {
  shaded?: boolean,
  join?: 'tangent' | 'miter' | 'round' | 'bevel',
} & Pick<Partial<PipelineOptions>, 'mode' | 'shadow' | 'depthTest' | 'depthWrite' | 'alphaToCoverage' | 'alphaToDiscard' | 'blend'>;

export type RawQuadsProps = {
  position?: VectorLike,
  rectangle?: VectorLike,
  color?: VectorLike,
  depth?: number,
  zBias?: number,
  uv?: VectorLike,
  st?: VectorLike,

  positions?: ShaderSource,
  rectangles?: ShaderSource,
  colors?: ShaderSource,
  depths?: ShaderSource,
  zBiases?: ShaderSource,
  uvs?: ShaderSource,
  sts?: ShaderSource,

  mask?: ShaderSource,
  raytrace?: ShaderSource,

  instance?: Lazy<number>,
  instances?: ShaderSource,
  transform?: TransformContextProps,

  count?: Lazy<number>,
} & FacetSource & PickingSource & RawQuadsFlags;

export const RawQuads: LiveComponent<RawQuadsProps> = memo((props: RawQuadsProps) => {
  const {
    alphaToCoverage,
    alphaToDiscard,
    depthTest,
    depthWrite,
    blend,
    mode = 'opaque',

    instance,
    instances,
    transform,

    count = null,
    shaded = false,
    shadow = false,
  } = props;

  const vertexCount = 4;
  const instanceCount = useDataLength(count, props.positions);

  const p = useSource(POSITIONS, useShaderRef(props.position, props.positions));
  const r = useShaderRef(props.rectangle, props.rectangles);
  const c = useShaderRef(props.color, props.colors);
  const d = useShaderRef(props.depth, props.depths);
  const z = useShaderRef(props.zBias, props.zBiases);
  const u = useShaderRef(props.uv, props.uvs);
  const s = useShaderRef(props.st, props.sts ?? p);

  const m = (mode !== 'debug') ? props.mask : null;
  const rt = (mode !== 'debug') ? props.raytrace : null;

  // Transformed positions and bounds
  const {positions, bounds: getBounds} = useApplyTransform(p, transform);

  let bounds: Lazy<DataBounds> | null = null;
  if (getBounds && (props.positions as any)?.bounds) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    bounds = useCallback(() => getBounds((props.positions! as any).bounds), [props.positions, getBounds]);
  }
  else {
    useNoCallback();
  }

  // Scissor mask
  const scissor = useScissorShader();

  // Solid or shaded material
  const renderer = shaded ? 'shaded' : 'solid';
  const material = useMaterialContext()[renderer];

  const solidVertex = useShader(getQuadVertex, [
    positions,
    r,
    c, d, z, u, s,
    instanceCount,
    scissor,
  ]);

  const shadedVertex = shaded ? useShader(getQuadVertexShaded, [
    positions,
    r,
    c, d, z, u, s,
    instanceCount,
    scissor,
  ]) : (useNoShader(), null);

  const [getVertex, totalCount, instanceDefs] = useInstancedVertex(shadedVertex ?? solidVertex, instance, instances, instanceCount);
  const [getVertexDepth] = shadow ? useInstancedVertex(solidVertex, instance, instances, instanceCount) : (useNoInstancedVertex(), [null]);

  const getPicking = usePickingShader(props);
  const getFacet = useFacetShader(props);

  // Shape mask (2D)
  const applyFragmentMask = m && material.getFragment ? useShader(getMaskedColor, [m]) : useNoShader();
  const getSurfaceMasked = !rt && m && material.getSurface ? useShader(getMaskedSurface, [m, material.getSurface]) : useNoShader();

  // Depth/normal mask via raytrace (3D)
  const getSurfaceRT = (
    rt && material.getSurface ? useShader(getRaytraceSurface, [material.getSurface, rt]) : useNoShader()
  );

  const links = useMemo(() => ({
    getVertex: shadow && !shaded ? chainTo(getVertex, solidToShaded) : getVertex,
    getPicking,
    getFacet,
    ...material,
    getVertexDepth,
    getSurface: getSurfaceRT ?? getSurfaceMasked ?? material.getSurface,
    getFragment: material.getFragment && applyFragmentMask ? chainTo(applyFragmentMask, material.getFragment) : material.getFragment,
  }), [getVertex, getVertexDepth, getPicking, getFacet, getSurfaceMasked, getSurfaceRT, applyFragmentMask, shadow, shaded, material]);

  const [pipeline, defs] = usePipelineOptions({
    mode,
    topology: 'triangle-strip',
    stripIndexFormat: 'uint16',
    side: 'both',
    shadow,
    alphaToCoverage,
    alphaToDiscard,
    depthTest,
    depthWrite,
    blend,
  });

  const defines: Record<string, any> = useMemo(() => ({
    ...defs,
    ...instanceDefs,
    HAS_DEPTH: shaded,
    HAS_EDGE_BLEED: true,
  }), [defs, instanceDefs, shaded]);

  return useDraw({
    vertexCount,
    instanceCount: totalCount,
    bounds,

    links,
    defines,

    renderer,
    pipeline,
    mode,
  });
}, 'RawQuads');
